<script lang="ts" setup>
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Line } from 'vue-chartjs';
import { ref, computed, watch } from 'vue';

const props = defineProps({
  // Array of objects with structure: { year: number, ...dataFields: number }
  data: {
    type: Array,
    required: true,
  },
  // Array of field names to display in the stacked area (e.g., ['reRent', 'sale', 'destroy', 'minorMaintenance', 'majorMaintenance'])
  fields: {
    type: Array,
    required: true,
  },
  // Optional: array of colors for each field
  colors: {
    type: Array,
    default: null,
  },
  // Optional: chart style
  chartStyle: {
    type: Object,
    default: () => ({}),
  },
  // Optional: custom label function
  labelFn: {
    type: Function,
    default: (fieldName) => fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
  },
});

const defaultColors = [
  'hsl(196 100% 63.9%)',
  'hsl(142 71% 65%)',
  'hsl(262 22% 87%)',
  'hsl(30 100% 60%)',
  'hsl(350 85% 60%)',
  'hsl(48 100% 60%)',
];

const chartData = ref({
  labels: [],
  datasets: [],
});

const getVisibleStackedMax = (chart) => {
  const datasets = chart?.data?.datasets || [];
  const labelCount = Array.isArray(chart?.data?.labels) ? chart.data.labels.length : 0;

  let maxTotal = 0;
  for (let index = 0; index < labelCount; index += 1) {
    let currentTotal = 0;
    datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) {
        return;
      }
      const pointValue = Array.isArray(dataset.data) ? Number(dataset.data[index]) || 0 : 0;
      currentTotal += pointValue;
    });
    maxTotal = Math.max(maxTotal, currentTotal);
  }

  return maxTotal;
};

const dynamicYAxisPlugin = {
  id: 'dynamicYAxisPlugin',
  beforeUpdate(chart) {
    const yScale = chart?.options?.scales?.y;
    if (!yScale || typeof yScale !== 'object') {
      return;
    }

    const visibleMax = getVisibleStackedMax(chart);
    const computedMax = visibleMax <= 1 ? 1 : Math.ceil(visibleMax / 10) * 10;
    const stepSize = visibleMax <= 1 ? 1 : 10;

    yScale.max = computedMax;
    yScale.suggestedMax = computedMax;
    if (!yScale.ticks || typeof yScale.ticks !== 'object') {
      yScale.ticks = {};
    }
    yScale.ticks.stepSize = stepSize;
  },
};

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: true,
  animation: {
    duration: 500,
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      onClick(event, legendItem, legend) {
        const defaultOnClick = ChartJS.defaults.plugins.legend.onClick;
        defaultOnClick(event, legendItem, legend);
        legend.chart.update();
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      filter: function () {
        // Show all datasets in tooltip, even hidden ones
        return true;
      },
      callbacks: {
        afterLabel: function () {
          // This will be called after each label in the tooltip
          return null;
        },
        footer: function (items: any) {
          // Calculate total for this year (including hidden datasets)
          if (items.length === 0) return '';
          
          let total = 0;
          items.forEach((item: any) => {
            total += item.parsed.y || 0;
          });
          
          return `Total: ${total.toLocaleString('nl-NL')}`;
        },
      },
    },
  },
  scales: {
    x: {
      stacked: false,
      title: {
        display: true,
        text: 'Jaar',
      },
      ticks: {
        callback: function (value : any, index: number) {
          // Show label every 2 years
          if (index % 2 === 0) {
            return this.getLabelForValue(value);
          }
          return '';
        },
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      title: {
        display: true,
        text: 'Aantal woningen',
      },
      ticks: {
        callback: function (value : any) {
          return Number(value).toFixed(0);
        },
      },
    },
  },
}));

// Generate datasets from data and fields
const generateDatasets = () => {
  const colors = props.colors || defaultColors;
  
  return props.fields.map((field, index) => {
    const color = colors[index % colors.length];
    const fieldData = props.data.map((item: any) => item[field] || 0);
    
    // Check if all values are 0
    const allZero = fieldData.every((value) => value === 0);
    
    return {
      label: props.labelFn(field),
      data: fieldData,
      borderColor: color,
      backgroundColor: color,
      fill: true,
      tension: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      pointBackgroundColor: color,
      pointBorderColor: '#fff',
      pointBorderWidth: 0,
      hidden: allZero, // Hide from chart but keep in legend
    };
  });
};

// Watch for data or fields changes
watch(
  () => [props.data, props.fields],
  () => {
    if (props.data && props.data.length > 0 && props.fields && props.fields.length > 0) {
      chartData.value = {
        labels: props.data.map((item: any) => item.year || item[Object.keys(item)[0]]),
        datasets: generateDatasets(),
      };
    }
  },
  { immediate: true, deep: true }
);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);
</script>

<template>
  <div class="stacked-area-chart-container">
    <Line :data="chartData" :options="chartOptions" :style="props.chartStyle" :plugins="[dynamicYAxisPlugin]" />
  </div>
</template>

<style lang="scss" scoped>
.stacked-area-chart-container {
  width: 100%;
  height: 100%;
}
</style>
